import React, { useEffect, useRef } from 'react';
import { X, Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import gsap from 'gsap';
import { useCart } from '../context/CartContext';
import { Button } from './ui/Button';
import { redirectToCheckout } from '../services/stripeService';
import { Loader2 } from 'lucide-react';

import { useAuth } from '../context/AuthContext';

export const CartDrawer = () => {
  const { cart, removeFromCart, updateQuantity, cartTotal, isCartOpen, setIsCartOpen } = useCart();
  const { user } = useAuth();
  const [isCheckingOut, setIsCheckingOut] = React.useState(false);
  const [orderNote, setOrderNote] = React.useState('');
  const [guestEmail, setGuestEmail] = React.useState('');
  const drawerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (isCartOpen) {
      document.body.style.overflow = 'hidden';
      gsap.to(overlayRef.current, { autoAlpha: 1, duration: 0.3 });
      gsap.to(drawerRef.current, { x: '0%', duration: 0.4, ease: 'power3.out' });
      
      // Stagger animate cart items
      if (itemsRef.current.length > 0) {
        gsap.fromTo(
          itemsRef.current.filter(Boolean),
          { y: 20, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.4, stagger: 0.05, ease: 'power2.out', delay: 0.2 }
        );
      }
    } else {
      document.body.style.overflow = '';
      gsap.to(overlayRef.current, { autoAlpha: 0, duration: 0.3 });
      gsap.to(drawerRef.current, { x: '100%', duration: 0.4, ease: 'power3.in' });
    }
  }, [isCartOpen, cart.length]);

  const handleCheckout = async () => {
    setIsCheckingOut(true);
    try {
      await redirectToCheckout(cart, user?.email || guestEmail, orderNote);
    } catch (error) {
      alert('Failed to initiate checkout. Please try again.');
    } finally {
      setIsCheckingOut(false);
    }
  };

  return (
    <>
      <div 
        ref={overlayRef}
        className="fixed inset-0 bg-black/50 z-[60] opacity-0 invisible backdrop-blur-sm"
        onClick={() => setIsCartOpen(false)}
      />
      <div 
        ref={drawerRef}
        className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-[70] shadow-2xl transform translate-x-full flex flex-col"
      >
        <div className="p-6 border-b flex items-center justify-between bg-secondary/10">
          <h2 className="font-serif text-2xl text-ink flex items-center gap-2">
            <ShoppingBag className="w-6 h-6" />
            Your Treats
          </h2>
          <button 
            onClick={() => setIsCartOpen(false)}
            className="p-3 hover:bg-black/5 rounded-full transition-colors"
            aria-label="Close cart"
          >
            <X className="w-6 h-6 text-ink" />
          </button>
        </div>

        <div className="px-6 py-4 bg-primary/5 border-b border-primary/10">
          {cart.length > 0 && cart.every(item => item.category === 'Gift Card') ? (
            <p className="text-sm text-primary-dark font-bold text-center flex items-center justify-center gap-2">
              <span>✉️</span> Digital Delivery is Free!
            </p>
          ) : 30 - cartTotal > 0 ? (
            <>
              <p className="text-sm text-ink font-medium mb-2 text-center">
                You're <span className="font-bold text-primary-dark">£{(30 - cartTotal).toFixed(2)}</span> away from <span className="font-bold">Free Shipping!</span>
              </p>
              <div className="w-full bg-stone-200 rounded-full h-2 overflow-hidden">
                <div 
                  className="bg-primary h-2 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${Math.min(100, (cartTotal / 30) * 100)}%` }}
                />
              </div>
            </>
          ) : (
            <p className="text-sm text-primary-dark font-bold text-center flex items-center justify-center gap-2">
              <span>🎉</span> You've unlocked Free Shipping!
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {cart.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center text-ink/60">
              <ShoppingBag className="w-16 h-16 mb-4 opacity-40" />
              <p className="font-sans text-lg font-bold">Your cart is empty.</p>
              <p className="text-sm">Time to fetch some goodies!</p>
            </div>
          ) : (
            <>
              <div className="space-y-6">
                {cart.map((item, index) => (
                  <div 
                    key={item.cartItemId} 
                    ref={el => itemsRef.current[index] = el}
                    className="flex gap-4 bg-white p-4 rounded-xl border border-stone-100 shadow-sm"
                  >
                    <div className="w-20 h-20 bg-stone-100 rounded-lg overflow-hidden flex-shrink-0">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 flex flex-col justify-between">
                      <div>
                        <h2 className="font-sans font-bold text-ink line-clamp-1">{item.name}</h2>
                        {item.selectedOptions && Object.entries(item.selectedOptions).length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {Object.entries(item.selectedOptions).map(([label, value], i) => (
                              <p key={`${label}-${i}`} className="text-[10px] text-ink/40 uppercase tracking-wider font-bold">
                                {label}: <span className="text-ink/60">{value}</span>
                              </p>
                            ))}
                          </div>
                        )}
                        <p className="text-primary font-bold mt-1">
                          £{item.price.toFixed(2)}
                          {item.isSubscription && <span className="text-ink/50 text-xs font-medium ml-1">/ {item.interval || 'month'}</span>}
                        </p>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-3 bg-stone-100 rounded-full px-2 py-1">
                          <button 
                            onClick={() => updateQuantity(item.cartItemId, item.quantity - 1)}
                            className="p-3 hover:text-primary transition-colors disabled:opacity-30"
                            disabled={item.quantity <= 1}
                            aria-label={`Decrease quantity of ${item.name}`}
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="font-mono text-sm w-4 text-center" aria-live="polite">{item.quantity}</span>
                          <button 
                            onClick={() => updateQuantity(item.cartItemId, item.quantity + 1)}
                            className="p-3 hover:text-primary transition-colors"
                            aria-label={`Increase quantity of ${item.name}`}
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        <button 
                          onClick={() => removeFromCart(item.cartItemId)}
                          className="text-red-400 hover:text-red-600 p-3"
                          aria-label={`Remove ${item.name} from cart`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Order Note Section */}
              <div className="mt-8 pt-6 border-t border-stone-100">
                <label htmlFor="orderNote" className="block text-sm font-bold text-ink/60 uppercase tracking-wider mb-2">
                  Order Note (Optional)
                </label>
                <textarea
                  id="orderNote"
                  rows={3}
                  placeholder="Special instructions or a gift message..."
                  value={orderNote}
                  onChange={(e) => setOrderNote(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-shadow text-sm resize-none bg-stone-50"
                />
                <p className="text-[10px] text-ink/40 mt-2 italic">
                  Adding a gift? Let us know here!
                </p>
              </div>
            </>
          )}
        </div>

        <div className="p-6 border-t bg-stone-50">
          {!user && cart.length > 0 && (
            <div className="mb-6 p-4 bg-white rounded-2xl border border-primary/10 shadow-sm">
              <p className="text-xs font-bold text-primary uppercase tracking-widest mb-2">🐾 Get 10% Off Your First Order</p>
              <input 
                type="email" 
                placeholder="Enter email for discount..." 
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
                className="w-full px-4 py-2 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
              />
              <p className="text-[10px] text-ink/40 mt-2">We'll also save your cart so you don't lose your treats!</p>
            </div>
          )}
          <div className="flex justify-between items-center mb-2">
            <span className="font-sans text-lg text-ink/80 font-medium">Total</span>
            <span className="font-serif text-3xl text-ink">£{cartTotal.toFixed(2)}</span>
          </div>
          {cart.some(item => item.isSubscription) && (
            <p className="text-xs text-ink/70 text-right mb-4">
              Includes recurring subscription items
            </p>
          )}
          <Button 
            variant="primary" 
            className="w-full py-4 text-xl shadow-lg mb-3"
            disabled={cart.length === 0 || isCheckingOut}
            onClick={handleCheckout}
          >
            {isCheckingOut ? (
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            ) : (
              'Checkout Now'
            )}
          </Button>
          <div className="flex items-center justify-center gap-1.5 text-ink/80 text-xs font-sans font-bold">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
            Secure 1-Click Checkout available
          </div>
        </div>
      </div>
    </>
  );
};
